import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { IconEye, IconRefresh, IconSend } from '@tabler/icons';

export default function SentFaxTable({ rows, onRefresh, onResend }) {
  const [search, setSearch] = useState('');
  const [selectedFax, setSelectedFax] = useState(null);

  const isNumericStatus = (status) => {
    const value = String(status ?? '').trim();
    return value !== '' && !Number.isNaN(Number(value));
  };

  const canRefreshStatus = (status) => String(status ?? '').trim() !== '0';

  const canResend = (item) => {
    if (!item?.providerFaxId) {
      return false;
    }

    const status = item.status;
    if (isNumericStatus(status)) {
      return Number(status) > 0;
    }

    const value = String(status || '').toLowerCase();
    return (
      value.includes('failed')
      || value.includes('error')
      || value.includes('cancel')
      || value.includes('rejected')
    );
  };

  const filteredRows = useMemo(() => {
    const value = search.toLowerCase().trim();
    if (!value) {
      return rows;
    }

    return rows.filter(item => {
      const haystack = `${item.recipient} ${item.faxNumber} ${item.details} ${item.status} ${item.statusLabel || ''}`.toLowerCase();
      return haystack.includes(value);
    });
  }, [rows, search]);

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4">Sent Fax</Typography>
            <Typography variant="body2" color="text.secondary">
              View recently sent fax referrals.
            </Typography>
          </Box>
          <TextField
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', md: 280 } }}
          />
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>Fax Number</TableCell>
                <TableCell>Details (Internal Notes)</TableCell>
                <TableCell>Date Sent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.recipient}</TableCell>
                  <TableCell>{item.faxNumber}</TableCell>
                  <TableCell>{item.details || '-'}</TableCell>
                  <TableCell>{item.dateSent ? new Date(item.dateSent).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={item.statusLabel || item.status} color="info" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<IconEye size={14} />}
                        onClick={() => setSelectedFax(item)}
                      >
                        View
                      </Button>
                      {canRefreshStatus(item.status) ? (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<IconRefresh size={14} />}
                          onClick={() => onRefresh?.(item.id)}
                        >
                          Refresh
                        </Button>
                      ) : null}
                      {canResend(item) ? (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<IconSend size={14} />}
                          onClick={() => onResend?.(item.id)}
                        >
                          Resend
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No sent faxes found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog
        open={Boolean(selectedFax)}
        onClose={() => setSelectedFax(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Sent Fax Details</DialogTitle>
        <DialogContent dividers>
          {selectedFax ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Fax To"
                  value={selectedFax.recipient || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Fax Number"
                  value={selectedFax.faxNumber || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Stack>

              <TextField
                label="Message"
                value={selectedFax.message || ''}
                fullWidth
                multiline
                minRows={3}
                InputProps={{ readOnly: true }}
              />

              <TextField
                label="Details (Internal Notes)"
                value={selectedFax.details || ''}
                fullWidth
                multiline
                minRows={3}
                InputProps={{ readOnly: true }}
              />

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Attachments
                </Typography>
                {selectedFax.attachmentNames?.length ? (
                  <Stack spacing={1}>
                    {selectedFax.attachmentNames.map((name, index) => (
                      <Box
                        key={`${name}-${index}`}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: 3,
                          px: 2,
                          py: 1,
                          backgroundColor: '#ffffff',
                          cursor: selectedFax.attachmentUrls?.[index] ? 'pointer' : 'default',
                          '&:hover': selectedFax.attachmentUrls?.[index]
                            ? {
                              backgroundColor: '#f8fafc',
                              borderColor: '#d1d5db',
                            }
                            : {},
                        }}
                        onClick={() => {
                          const url = selectedFax.attachmentUrls?.[index];
                          if (url) {
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        <Typography variant="body2">{name}</Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No attachments saved.
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedFax(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
